// src/features/merchantConfig/hooks/useMerchantConfig.js
//
// TanStack Query wrapper for a single (merchantId, configKey) pair.
//
// Retry policy per the UI prompt §6:
//   * RECORD_NOT_FOUND (404) is a first-class state -> NO retry.
//   * 5xx / network errors -> 2 retries with exponential backoff.
//   * `staleTime` is 60s because the backend is also Spring-cached and
//     a fresh GET right after read is effectively free.

import { useQuery } from "@tanstack/react-query";
import { fetchMerchantConfig } from "../api/merchantConfigClient";

/**
 * Compose the TanStack Query key for a single config document.
 *
 * Exposed so that Phase 2's `useMutation({ onSuccess })` can call
 * `queryClient.setQueryData(merchantConfigQueryKey(...), updated)` without
 * having to know the internal key shape.
 *
 * @param {string} merchantId
 * @param {string} configKey
 */
export const merchantConfigQueryKey = (merchantId, configKey) => [
  "merchantConfig",
  merchantId,
  configKey,
];

/**
 * @param {{ merchantId: string | null | undefined, configKey: string | null | undefined, enabled?: boolean }} options
 */
export function useMerchantConfig({ merchantId, configKey, enabled = true }) {
  const isReady = Boolean(merchantId && configKey);

  return useQuery({
    queryKey: merchantConfigQueryKey(merchantId, configKey),
    queryFn: ({ signal }) =>
      fetchMerchantConfig({ merchantId, configKey, signal }),
    enabled: enabled && isReady,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      // Do NOT retry on 404 / "not provisioned" - it's a real state.
      if (error?.isNotFound) return false;
      // Validation errors are deterministic - also no retry.
      if (error?.status && error.status >= 400 && error.status < 500) return false;
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1_000 * 2 ** attemptIndex, 8_000),
  });
}
